import { useCallback, useState } from 'react';

const STORAGE_KEY = 'homepoint.nav.expanded';

/** Below this viewport width the rail starts collapsed unless the user pinned it open. */
const EXPANDED_BY_DEFAULT_MIN_WIDTH = 1440;

const readStored = (): boolean | null => {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === null) return null;
    return stored === 'true';
  } catch {
    return null;
  }
};

/**
 * Whether the desktop nav rail is pinned open. The choice persists per
 * browser; with nothing stored, wide screens start expanded and everything
 * narrower starts as the icon rail so the page keeps its room.
 */
export default function useNavExpanded(): [boolean, () => void] {
  const [expanded, setExpanded] = useState<boolean>(() => {
    const stored = readStored();
    if (stored !== null) return stored;
    return typeof window !== 'undefined' && window.innerWidth >= EXPANDED_BY_DEFAULT_MIN_WIDTH;
  });

  const toggle = useCallback(() => {
    setExpanded((current) => {
      const next = !current;
      try {
        window.localStorage.setItem(STORAGE_KEY, String(next));
      } catch {
        // Private mode or a full store: the rail still toggles for this session.
      }
      return next;
    });
  }, []);

  return [expanded, toggle];
}
